import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { HiOutlineMagnifyingGlass, HiOutlinePencil, HiOutlineTrash, HiOutlineNewspaper, HiOutlineCalendar } from 'react-icons/hi2';
import { useAdmin } from '../context/AdminContext';
import { PositionControls } from './PositionControls';
import AlertDialog from '../../common/AlertDialog';

const API_URL = import.meta.env.VITE_API_URL;

export function NewsList({ onEdit, onDelete }) {
  const { t, i18n } = useTranslation();
  const { news, handleDelete, handleNewsPositionUpdate, loading } = useAdmin();
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState('');
  const [filteredNews, setFilteredNews] = useState([]);
  const [visibleCount, setVisibleCount] = useState(50);
  const [sortMode, setSortMode] = useState(false);
  const [positionInputs, setPositionInputs] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);

  // Filter news based on search
  useEffect(() => {
    if (!Array.isArray(news)) {
      setFilteredNews([]);
      return;
    }

    if (!search.trim()) {
      setFilteredNews(news);
    } else {
      const searchLower = search.toLowerCase();
      const filtered = news.filter(n => 
        (n.text_de && n.text_de.toLowerCase().includes(searchLower)) ||
        (n.text_en && n.text_en.toLowerCase().includes(searchLower))
      );
      setFilteredNews(filtered);
    }
  }, [news, search]);

  // Position Update Handler
  const handlePositionUpdateWrapper = async (newsId, newPosition) => {
    if (isUpdating) return;
    
    setIsUpdating(true);
    
    try {
      await handleNewsPositionUpdate(newsId, newPosition);
    } catch (error) {
      alert(t('newslist.position_error', 'Fehler beim Aktualisieren der Position'));
    } finally {
      setIsUpdating(false);
    }
  };

  // Handler für das Löschen mit Dialog
  const handleDeleteRequest = (id) => setDeleteId(id);
  const handleCancelDelete = () => setDeleteId(null);
  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    try {
      await handleDelete('news', deleteId);
      setDeleteId(null);
    } catch (err) {
      console.error('Error deleting news:', err);
      alert('Fehler beim Löschen der News');
    }
  };

  // Helper to format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  return (
    <div className="mt-10">
      <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">{t('news.list')}</h3>
      
      {/* Header mit Suchleiste und Sortier-Toggle */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        {/* Search Bar */}
        <div className="flex-1 flex items-center gap-2 bg-white dark:bg-zinc-800 rounded-lg shadow px-3 py-2 border border-gray-200 dark:border-zinc-700">
          <HiOutlineMagnifyingGlass className="w-5 h-5 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            className="flex-1 bg-transparent outline-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 text-sm"
            placeholder={t('news.search_placeholder', 'Suche in News...')}
            value={search}
            onChange={e => { setSearch(e.target.value); setVisibleCount(50); }}
          />
        </div>
        {/* Sortier-Modus Toggle */}
        <button
          onClick={() => setSortMode(!sortMode)}
          disabled={isUpdating}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
            sortMode 
              ? 'bg-green-600 text-white shadow-lg' 
              : 'bg-white dark:bg-zinc-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-zinc-700 shadow'
          }`}
        >
          {isUpdating && sortMode && <span className="mr-2">⟲</span>}
          {sortMode ? t('newslist.sort_mode_on', 'Sortier-Modus aktiv') : t('newslist.sort_mode_off', 'Sortier-Modus')}
        </button>
      </div>

      {loading ? (
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900 dark:border-gray-100 mx-auto" />
      ) : (!filteredNews || filteredNews.length === 0) ? (
        <div className="text-gray-500 text-center">{search ? t('news.no_search_results', 'Keine News gefunden') : t('news.no_news')}</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
          {filteredNews.slice(0, visibleCount).map(n => (
            <div key={n.id} className="flex flex-col h-full">
              {/* Main Card */}
              <div className="bg-white dark:bg-zinc-800 rounded-lg shadow hover:shadow-lg transition-shadow duration-200 border border-gray-200 dark:border-zinc-700 hover:border-green-200 dark:hover:border-green-700 flex flex-col h-full relative">
                {/* Position Badge (only in sort mode) */}
                {sortMode && (
                  <div className="absolute top-2 right-2 bg-green-600 text-white text-xs font-bold px-2 py-1 rounded-full z-10">
                    #{n.sort_order}
                  </div>
                )}

                {/* Media Section */}
                {(Array.isArray(n.images) && n.images.length > 0) || (Array.isArray(n.videos) && n.videos.length > 0) ? (
                  <div className="relative w-full h-24 md:h-32 bg-gray-100 dark:bg-zinc-900 flex items-center justify-center overflow-hidden rounded-t-lg flex-shrink-0">
                    {/* Show first image or video as hero */}
                    {Array.isArray(n.images) && n.images.length > 0 ? (
                      <img
                        src={n.images[0].startsWith('http') ? n.images[0] : `${API_URL}${n.images[0]}`}
                        alt="News"
                        className="w-full h-full object-cover"
                      />
                    ) : Array.isArray(n.videos) && n.videos.length > 0 ? (
                      <div className="relative w-full h-full">
                        <video
                          src={`${n.videos[0].startsWith('http') ? n.videos[0] : `${API_URL}${n.videos[0]}`}#t=0.1`}
                          className="w-full h-full object-cover"
                          muted
                          playsInline
                          preload="metadata"
                          controls={false}
                        />
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <svg className="w-6 h-6 md:w-8 md:h-8 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="12" fill="black" fillOpacity="0.4" />
                            <polygon points="10,8 16,12 10,16" fill="white" />
                          </svg>
                        </div>
                      </div>
                    ) : null}
                    
                    {/* Media count badge */}
                    {((Array.isArray(n.images) ? n.images.length : 0) + (Array.isArray(n.videos) ? n.videos.length : 0)) > 1 && (
                      <div className="absolute bottom-1 right-1 bg-black/70 text-white px-1.5 py-0.5 rounded-full text-xs">
                        +{((Array.isArray(n.images) ? n.images.length : 0) + (Array.isArray(n.videos) ? n.videos.length : 0)) - 1}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="relative w-full h-24 md:h-32 bg-gray-100 dark:bg-zinc-900 flex items-center justify-center rounded-t-lg flex-shrink-0">
                    <HiOutlineNewspaper className="w-8 h-8 md:w-12 md:h-12 text-gray-400 dark:text-zinc-600" />
                  </div>
                )}
                
                {/* Content Section */}
                <div className="p-2 md:p-3 flex flex-col gap-2 flex-grow">
                  {/* Date */}
                  <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                    <HiOutlineCalendar className="w-3 h-3 flex-shrink-0" />
                    <span>{formatDate(n.created_at)}</span>
                  </div>
                  
                  {/* Text Content */}
                  <div className="text-gray-600 dark:text-gray-300 text-xs md:text-sm line-clamp-3 flex-grow">
                    {i18n.language.startsWith('de') ? n.text_de : n.text_en}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-1 md:gap-2 mt-auto pt-1">
                    <button 
                      onClick={() => onEdit && onEdit(n)} 
                      className="flex-1 px-2 md:px-3 py-1 md:py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs md:text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDeleteRequest(n.id)} 
                      className="flex-1 px-2 md:px-3 py-1 md:py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-xs md:text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      Del
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Position Controls */}
              <PositionControls
                item={n}
                totalItems={news.length}
                positionInputs={positionInputs}
                setPositionInputs={setPositionInputs}
                onPositionUpdate={handlePositionUpdateWrapper}
                isUpdating={isUpdating}
                sortMode={sortMode}
              />
            </div>
          ))}
        </div>
      )}
      
      {/* Load More Button */}
      {filteredNews.length > visibleCount && (
        <button
          className="mt-4 mx-auto px-6 py-2 bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 rounded-full shadow hover:bg-green-200 dark:hover:bg-green-800 transition-all"
          onClick={() => setVisibleCount(c => c + 50)}
        >
          {t('newslist.load_more', 'Mehr laden')} ({filteredNews.length - visibleCount} weitere)
        </button>
      )}

      {/* AlertDialog for delete confirmation */}
      <AlertDialog
        open={!!deleteId}
        title={t('news.delete_title', 'News löschen?')}
        description={t('news.delete_confirm_generic', 'Möchtest du diese News wirklich löschen?')}
        confirmText={t('news.delete', 'Löschen')}
        cancelText={t('news.cancel', 'Abbrechen')}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmColor="red"
      />
    </div>
  );
}